import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useConfirm } from "./ConfirmDialog";

// Sección Usuarios: los usuarios administradores de cada portal.
//   - mi.eneural.org       → staff (el rol decide el acceso: ROLE_RANK del backend).
//   - clientes.eneural.org → perfil admin (rol `sac`, ABM de clientes) o usuario
//                            (`sac_lectura`, solo lectura), acotados a EMPRESAS.
//   - contabilidad         → otra aplicación con usuarios propios: fuera por ahora.
// El filtro por empresa lo aplica el BACKEND (middleware + listado): esto es solo la UI.

const ROLES_MI = [
  { key:'superadmin', label:'Super Administrador', color:'var(--danger)',  bg:'var(--danger-bg)',
    desc:'Todo el panel, incluida la gestión de administradores.' },
  { key:'admin',      label:'Administrador',       color:'var(--brand)',   bg:'var(--brand-subtle)',
    desc:'Todo el panel técnico. Puede crear usuarios de los portales de clientes.' },
  { key:'agent',      label:'Agente',              color:'var(--info)',    bg:'var(--info-bg)',
    desc:'Alta y edición de clientes; ve solo los clientes que dio de alta.' },
  { key:'cajero',     label:'Cajero',              color:'var(--success)', bg:'var(--success-bg)',
    desc:'Recaudación.' },
  { key:'tecnico',    label:'Auditor técnico',     color:'var(--text-muted)', bg:'var(--bg-hover)',
    desc:'Lecturas técnicas puntuales (rechazos, historial de ANI). No escribe.' },
];

const PERFILES_CLIENTES = [
  { key:'sac',           label:'Admin',         color:'var(--brand)', bg:'var(--brand-subtle)',
    desc:'Alta, baja y modificación de los clientes de sus empresas. No ve credenciales SIP.' },
  { key:'sac_lectura',   label:'Usuario',       color:'var(--info)',  bg:'var(--info-bg)',
    desc:'Solo VE los clientes de sus empresas. No ve credenciales SIP.' },
  { key:'implementador', label:'Implementador', color:'var(--warning)', bg:'var(--warning-bg)',
    desc:'Solo lectura + credenciales SIP, para configurar el equipo en sitio.' },
];

const ROLES_CLIENTES = new Set(PERFILES_CLIENTES.map(p => p.key));

const EMPRESAS = [
  { key:'netvoice', label:'Netvoice' },
  { key:'netlife',  label:'Netlife'  },
  { key:'telconet', label:'Telconet' },
];

const PORTALES = [
  { key:'mi',           label:'mi.eneural.org' },
  { key:'clientes',     label:'clientes.eneural.org' },
  { key:'contabilidad', label:'contabilidad.eneural.org' },
];

function portalDe(role) { return ROLES_CLIENTES.has(role) ? 'clientes' : 'mi'; }

function roleInfo(key) {
  return [...ROLES_MI, ...PERFILES_CLIENTES].find(r => r.key === key)
    || { label: key === 'intranet' ? 'Servicio (intranet)' : key, color:'var(--text-muted)', bg:'var(--bg-hover)' };
}

function RoleBadge({ role }) {
  const r = roleInfo(role);
  return <span className="nv-badge" style={{ background:r.bg, color:r.color }}>{r.label}</span>;
}

function empresasLista(csv) { return csv ? csv.split(',').filter(Boolean) : []; }

function EmpresasBadges({ csv }) {
  const lista = empresasLista(csv);
  if (!lista.length) return <span className="nv-badge nv-badge-ok">Todas</span>;
  return (
    <span style={{ display:'inline-flex', gap:4, flexWrap:'wrap' }}>
      {lista.map(e => (
        <span key={e} className="nv-badge" style={{ background:'var(--bg-hover)', color:'var(--text-secondary)' }}>
          {(EMPRESAS.find(x => x.key === e) || { label:e }).label}
        </span>
      ))}
    </span>
  );
}

function Alert({ msg, onClose }) {
  if (!msg) return null;
  const cls = { success:'nv-alert-ok', error:'nv-alert-err', warn:'nv-alert-warn' }[msg.type] || 'nv-alert-ok';
  return (
    <div className={`nv-alert ${cls}`} style={{ marginBottom:14 }}>
      <span style={{ flex:1 }}>{msg.text}</span>
      <button onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',color:'inherit',fontSize:14 }}>✕</button>
    </div>
  );
}

/* ── Selector de empresas: "Todas" o cualquier combinación ── */
function SelectorEmpresas({ value, onChange }) {
  const todas = value.length === 0;
  const toggle = (k) => {
    const next = value.includes(k) ? value.filter(x => x !== k) : [...value, k];
    // Marcar las tres equivale a "Todas" (y así lo guarda el backend).
    onChange(next.length === EMPRESAS.length ? [] : next);
  };
  return (
    <div className="nv-form-field">
      <label className="nv-label">Empresas que puede ver *</label>
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', alignItems:'center', padding:'6px 0' }}>
        <label style={{ display:'flex', gap:6, alignItems:'center', fontSize:12, cursor:'pointer' }}>
          <input type="checkbox" checked={todas} onChange={() => onChange([])} />
          <b>Todos los clientes</b>
        </label>
        {EMPRESAS.map(e => (
          <label key={e.key} style={{ display:'flex', gap:6, alignItems:'center', fontSize:12, cursor:'pointer' }}>
            <input type="checkbox" checked={!todas && value.includes(e.key)} onChange={() => toggle(e.key)} />
            Clientes {e.label}
          </label>
        ))}
      </div>
      <div style={{ fontSize:10, color:'var(--text-muted)' }}>
        Los clientes dados de alta desde el panel cuentan como Netvoice.
      </div>
    </div>
  );
}

/* ── Modal crear / editar ── */
function ModalUsuario({ portal, user, onClose, onSave }) {
  const esNuevo = !user;
  const opciones = portal === 'clientes' ? PERFILES_CLIENTES : ROLES_MI;
  const [form, setForm] = useState({
    username: user?.username || '',
    nombre_completo: user?.nombre_completo || '',
    email: user?.email || '',
    role: user?.role || (portal === 'clientes' ? 'sac_lectura' : 'agent'),
    empresas: empresasLista(user?.empresas),
    password: '', password_confirm: '',
  });
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const set = (k,v) => setForm(f => ({...f,[k]:v}));
  const rolActual = opciones.find(o => o.key === form.role);

  const submit = async () => {
    if (esNuevo && (!form.username || !form.password)) return setErr('Usuario y contraseña son requeridos');
    if (form.password && form.password.length < 8) return setErr('La contraseña debe tener al menos 8 caracteres');
    if (form.password && form.password !== form.password_confirm) return setErr('Las contraseñas no coinciden');
    setBusy(true); setErr(null);
    const payload = { nombre_completo: form.nombre_completo, email: form.email, role: form.role };
    if (portal === 'clientes') payload.empresas = form.empresas;
    try {
      if (esNuevo) {
        await api.post('/usuarios', { ...payload, username: form.username, password: form.password });
        onSave('Usuario creado');
      } else {
        if (form.password) payload.password = form.password;
        await api.put(`/usuarios/${user.id}`, payload);
        onSave('Usuario actualizado');
      }
    } catch(e) { setErr(e?.response?.data?.detail || 'Error al guardar'); }
    finally { setBusy(false); }
  };

  return (
    <div className="nv-modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="nv-modal">
        <div className="nv-modal-header">
          <span className="nv-modal-title">
            {esNuevo ? 'Nuevo usuario' : `Editar usuario — ${user.username}`}
            <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:400, marginLeft:8 }}>
              {(PORTALES.find(p => p.key === portal) || {}).label}
            </span>
          </span>
          <button className="nv-modal-close" onClick={onClose}>✕</button>
        </div>
        {err && <div className="nv-alert nv-alert-err" style={{ marginBottom:14 }}>{err}</div>}
        <div className="nv-form-row">
          <div className="nv-form-field">
            <label className="nv-label">Username *</label>
            <input className="nv-input" value={form.username} disabled={!esNuevo}
              onChange={e => set('username', e.target.value.toLowerCase().replace(/\s/g,''))}
              placeholder="ej: jperez" />
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Nombre completo</label>
            <input className="nv-input" value={form.nombre_completo}
              onChange={e => set('nombre_completo', e.target.value)} placeholder="Juan Pérez" />
          </div>
        </div>
        <div className="nv-form-row">
          <div className="nv-form-field">
            <label className="nv-label">Correo</label>
            <input className="nv-input" type="email" value={form.email}
              onChange={e => set('email', e.target.value)} placeholder="jperez@empresa.ec" />
          </div>
          <div className="nv-form-field">
            <label className="nv-label">{portal === 'clientes' ? 'Perfil *' : 'Rol *'}</label>
            <select className="nv-select" value={form.role} onChange={e => set('role', e.target.value)}>
              {opciones.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              {!rolActual && <option value={form.role}>{roleInfo(form.role).label}</option>}
            </select>
          </div>
        </div>
        {rolActual && (
          <div style={{ background:'var(--bg-raised)', borderRadius:'var(--r-sm)', padding:'8px 12px',
                        marginBottom:14, fontSize:11, color:'var(--text-secondary)' }}>
            {rolActual.desc}
          </div>
        )}
        {portal === 'clientes' && (
          <div style={{ marginBottom:14 }}>
            <SelectorEmpresas value={form.empresas} onChange={v => set('empresas', v)} />
          </div>
        )}
        <div style={{ borderTop:'1px solid var(--border)', paddingTop:14, marginBottom:14 }}>
          <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.07em', fontWeight:700, marginBottom:10 }}>
            {esNuevo ? 'Contraseña' : 'Cambiar contraseña (opcional)'}
          </div>
          <div className="nv-form-row">
            <div className="nv-form-field">
              <label className="nv-label">{esNuevo ? 'Contraseña *' : 'Nueva contraseña'}</label>
              <input className="nv-input" type={showPwd?'text':'password'} value={form.password}
                onChange={e => set('password', e.target.value)}
                placeholder={esNuevo ? 'Mín. 8 caracteres' : 'Dejar vacío para no cambiar'} />
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Confirmar contraseña</label>
              <input className="nv-input" type={showPwd?'text':'password'} value={form.password_confirm}
                onChange={e => set('password_confirm', e.target.value)} placeholder="Repetir contraseña" />
            </div>
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'var(--text-muted)', cursor:'pointer' }}>
            <input type="checkbox" checked={showPwd} onChange={e => setShowPwd(e.target.checked)} />
            Mostrar contraseña
          </label>
        </div>
        <div className="nv-modal-footer">
          <button className="nv-btn nv-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="nv-btn nv-btn-primary" onClick={submit} disabled={busy}>
            {busy ? <span className="nv-spinner" /> : (esNuevo ? '+ Crear usuario' : '✓ Guardar cambios')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Componente principal ── */
export default function Usuarios() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [portal,  setPortal]  = useState('clientes');
  const [modal,   setModal]   = useState(null);   // null | 'crear' | user (editar)
  const [msg,     setMsg]     = useState(null);
  const [search,  setSearch]  = useState('');
  const [confirmar, dialogoConfirm] = useConfirm();

  const showMsg = (text, type='success') => { setMsg({text,type}); setTimeout(()=>setMsg(null),4000); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get('/usuarios');
      setData(r.data.data || []);
    } catch(e) {
      setData([]);
      setError(e?.response?.data?.detail || 'No se pudo cargar la lista de usuarios');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isActivo = (u) => u.activo === 'yes' || u.activo === true;

  const handleToggle = async (user) => {
    if (isActivo(user) && !(await confirmar(`¿Desactivar el usuario ${user.username}? No podrá iniciar sesión.`))) return;
    try {
      const r = await api.put(`/usuarios/${user.id}/toggle`);
      showMsg(r?.data?.message || 'Estado actualizado');
      load();
    } catch(e) { showMsg(e?.response?.data?.detail || 'Error al cambiar el estado', 'error'); }
  };

  const handleDelete = async (user) => {
    if (!(await confirmar(`¿Eliminar TOTALMENTE el usuario ${user.username}?\n\nEsta acción es permanente y no se puede deshacer.`))) return;
    try {
      await api.delete(`/usuarios/${user.id}`);
      showMsg(`Usuario ${user.username} eliminado`);
      load();
    } catch(e) { showMsg(e?.response?.data?.detail || 'Error al eliminar', 'error'); }
  };

  const delPortal = data.filter(u => portalDe(u.role) === portal);
  const q = search.toLowerCase();
  const filtered = delPortal.filter(u =>
    !q || u.username?.toLowerCase().includes(q) || u.nombre_completo?.toLowerCase().includes(q));
  const cuenta = (p) => data.filter(u => portalDe(u.role) === p).length;

  return (
    <div>
      {dialogoConfirm}
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">Usuarios</div>
          <div className="nv-page-sub">Usuarios administradores de cada portal</div>
        </div>
        <div className="nv-page-actions">
          <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={load} disabled={loading}>
            {loading ? <span className="nv-spinner" /> : '↺'} Actualizar
          </button>
          {portal !== 'contabilidad' && (
            <button className="nv-btn nv-btn-primary" onClick={() => setModal('crear')}>+ Nuevo usuario</button>
          )}
        </div>
      </div>

      {/* Pestañas por portal */}
      <div style={{ display:'flex', gap:4, borderBottom:'1px solid var(--border)', marginBottom:16, flexWrap:'wrap' }}>
        {PORTALES.map(p => (
          <button key={p.key} onClick={() => { setPortal(p.key); setSearch(''); }}
            style={{
              background:'none', border:'none', cursor:'pointer', padding:'8px 14px', fontSize:12,
              color: portal===p.key ? 'var(--brand)' : 'var(--text-secondary)',
              borderBottom: `2px solid ${portal===p.key ? 'var(--brand)' : 'transparent'}`,
              fontWeight: portal===p.key ? 700 : 500,
            }}>
            {p.label}{p.key !== 'contabilidad' && <span style={{ opacity:.6 }}> · {cuenta(p.key)}</span>}
          </button>
        ))}
      </div>

      <Alert msg={msg} onClose={() => setMsg(null)} />
      {error && <div className="nv-alert nv-alert-err" style={{ marginBottom:14 }}>{error}</div>}

      {portal === 'contabilidad' ? (
        <div className="nv-card" style={{ padding:'28px 20px', textAlign:'center', color:'var(--text-muted)', fontSize:12 }}>
          contabilidad.eneural.org es otra aplicación, con sus propios usuarios.
          Todavía no se administra desde acá.
        </div>
      ) : (
        <>
          {portal === 'clientes' && (
            <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:12 }}>
              <b>Admin</b>: alta, baja y modificación de clientes. <b>Usuario</b>: solo consulta.
              Los dos ven únicamente los clientes de las empresas asignadas.
            </div>
          )}
          <div style={{ marginBottom:14 }}>
            <div className="nv-search">
              <span style={{ color:'var(--text-muted)', fontSize:12 }}>⌕</span>
              <input placeholder="Buscar por username o nombre..."
                value={search} onChange={e => setSearch(e.target.value)} />
              {search && <button onClick={() => setSearch('')}
                style={{ background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer' }}>✕</button>}
            </div>
          </div>

          <div className="nv-card" style={{ padding:0 }}>
            {loading ? (
              <div className="nv-loading"><span className="nv-spinner" /></div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign:'center', padding:'48px 20px' }}>
                <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:16 }}>
                  {search ? 'Sin resultados' : 'Este portal todavía no tiene usuarios'}
                </div>
                {!search && <button className="nv-btn nv-btn-primary" onClick={() => setModal('crear')}>+ Crear usuario</button>}
              </div>
            ) : (
              <div className="nv-table-wrap">
                <table className="nv-table">
                  <thead><tr>
                    <th>Usuario</th><th>Nombre</th><th>{portal === 'clientes' ? 'Perfil' : 'Rol'}</th>
                    {portal === 'clientes' && <th>Empresas</th>}
                    <th>Estado</th><th>Último acceso</th><th>Acciones</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(u => (
                      <tr key={u.id}>
                        <td><span className="mono" style={{ color:'var(--text-primary)', fontWeight:500 }}>{u.username}</span></td>
                        <td style={{ color:'var(--text-secondary)' }}>
                          {u.nombre_completo || '—'}
                          {u.email && <div style={{ fontSize:10, color:'var(--text-muted)' }}>{u.email}</div>}
                        </td>
                        <td><RoleBadge role={u.role} /></td>
                        {portal === 'clientes' && <td><EmpresasBadges csv={u.empresas} /></td>}
                        <td>
                          <span className={`nv-badge ${isActivo(u) ? 'nv-badge-ok' : 'nv-badge-muted'}`}>
                            <span className="dot" />{isActivo(u) ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td style={{ fontSize:10, color:'var(--text-muted)' }}>
                          {u.last_login ? new Date(u.last_login).toLocaleString('es-EC',{dateStyle:'short',timeStyle:'short'}) : '—'}
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:8 }}>
                            <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={() => setModal(u)} title="Editar usuario">✎</button>
                            <button className={`nv-btn nv-btn-sm ${isActivo(u) ? 'nv-btn-secondary' : 'nv-btn-primary'}`}
                              onClick={() => handleToggle(u)} title={isActivo(u) ? 'Desactivar usuario' : 'Activar usuario'}>
                              {isActivo(u) ? '⏸' : '▶'}
                            </button>
                            <button className="nv-btn nv-btn-danger nv-btn-sm" onClick={() => handleDelete(u)}
                              title="Eliminar usuario">✕</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ padding:'8px 14px', borderTop:'1px solid var(--border)', fontSize:10, color:'var(--text-muted)', textAlign:'center' }}>
                  {filtered.length} de {delPortal.length} usuarios
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {modal && (
        <ModalUsuario portal={portal} user={modal === 'crear' ? null : modal}
          onClose={() => setModal(null)}
          onSave={(m) => { setModal(null); load(); showMsg(m); }} />
      )}
    </div>
  );
}
